/**
 * HTTP-клиент для IMS Medical Agent API.
 *
 * Two productions (same build, pick by hostname):
 * - Ateshgah: progress-agent-api.ateshgah.com + key aT4rH8mQ2pL5
 * - Progress: medical-agent-api.progress-ims.com + key bU5sI8nR3qM6
 * Local / unknown: Progress.
 *
 * Overrides: VUE_APP_MEDICAL_AGENT_API, VUE_APP_MEDICAL_AGENT_API_KEY
 */
import axios from "axios";
import CryptoJS from "crypto-js";
import Utils from "@/project/Source/utils.js";

const CRYPT_KEY = "secret_key";

const MEDICAL_AGENT_ATESHGAH = {
  name: "ateshgah",
  apiBase: "https://progress-agent-api.ateshgah.com/api",
  apiKey: "aT4rH8mQ2pL5",
};

const MEDICAL_AGENT_PROGRESS = {
  name: "progress",
  apiBase: "https://medical-agent-api.progress-ims.com/api",
  apiKey: "bU5sI8nR3qM6",
};

function resolveMedicalAgentConfig() {
  const envApi = process.env.VUE_APP_MEDICAL_AGENT_API;
  if (envApi != null && String(envApi).trim() !== "") {
    return {
      name: "env",
      apiBase: String(envApi).trim().replace(/\/+$/, ""),
      apiKey:
        (process.env.VUE_APP_MEDICAL_AGENT_API_KEY != null &&
          String(process.env.VUE_APP_MEDICAL_AGENT_API_KEY).trim()) ||
        MEDICAL_AGENT_PROGRESS.apiKey,
    };
  }

  if (typeof window === "undefined") {
    return process.env.NODE_ENV === "production"
      ? MEDICAL_AGENT_ATESHGAH
      : MEDICAL_AGENT_PROGRESS;
  }

  const host = String(window.location.hostname || "").toLowerCase();
  if (host.includes("ateshgah")) return MEDICAL_AGENT_ATESHGAH;
  // localhost and progress-ims.com (and any other Progress deploy) → Progress API
  return MEDICAL_AGENT_PROGRESS;
}

const medicalAgentConfig = resolveMedicalAgentConfig();

/** Resolved API base (Ateshgah / Progress / env override). */
export const medicalAgentApiBase = medicalAgentConfig.apiBase;

const MEDICAL_AGENT_API_KEY = medicalAgentConfig.apiKey;

if (typeof console !== "undefined" && console.info) {
  console.info("[MedicalAgent]", medicalAgentConfig.name, medicalAgentApiBase);
}

const LS_USER_BRANCH = "medical_agent_user_branch";
const LS_USER_BY_ROLE = "medical_agent_user_by_role";

function decryptJwtToken() {
  const jwt = window.localStorage.getItem("jwt");
  if (!jwt) return null;
  try {
    const raw = CryptoJS.AES.decrypt(jwt, CRYPT_KEY).toString(CryptoJS.enc.Utf8);
    if (!raw || !String(raw).trim()) return null;
    const token = JSON.parse(raw);
    if (token == null) return null;
    const s = typeof token === "string" ? token.trim() : String(token).trim();
    if (!s || s.toLowerCase() === "none") return null;
    return s;
  } catch {
    return null;
  }
}

function shouldShowAlert(config) {
  return !(config && config.silent === true);
}

function envelopeSuccess(response) {
  const body = response && response.data;
  if (body && typeof body.isSuccess === "boolean") {
    if (body.isSuccess) return response;
    const message = Utils.FormatErrorWithCorrelationId(null, response);
    if (shouldShowAlert(response.config)) {
      Utils.Alert(message);
    }
    return Promise.reject(new Error(message || "Medical Agent: request failed"));
  }
  return response;
}

function envelopeError(error) {
  if (error.response?.status === 401) {
    Utils.redirectToLoginOnUnauthorized();
    return Promise.reject(error);
  }
  const message = Utils.FormatErrorWithCorrelationId(error);
  if (shouldShowAlert(error.config)) {
    Utils.Alert(message);
  }
  return Promise.reject(error);
}

let publicClient;
function getPublicClient() {
  if (!publicClient) {
    publicClient = axios.create({
      baseURL: medicalAgentApiBase,
      withCredentials: false,
    });
    publicClient.interceptors.request.use((config) => Utils.applyRequestCorrelationId(config));
    publicClient.interceptors.response.use((r) => r, envelopeError);
  }
  return publicClient;
}

let authClient;
function getAuthClient() {
  if (!authClient) {
    authClient = axios.create({
      baseURL: medicalAgentApiBase,
      withCredentials: false,
    });
    authClient.interceptors.request.use((config) => {
      const token = decryptJwtToken();
      if (token) {
        config.headers.Authorization = "Bearer " + token;
      }
      return Utils.applyRequestCorrelationId(config);
    });
    authClient.interceptors.response.use(envelopeSuccess, envelopeError);
  }
  return authClient;
}

/** Login — без JWT; обязателен X-API-Key. */
export function Login(payload) {
  return getPublicClient().post("SystemAuth/Login", payload, {
    headers: {
      Accept: "text/plain",
      "X-API-Key": MEDICAL_AGENT_API_KEY,
      "Content-Type": "application/json",
    },
  });
}

export function CheckJWT(payload) {
  return getAuthClient().post("SystemAuth/CheckJWT", payload);
}

export function ChangePassword(payload) {
  return getAuthClient().post("SystemAuth/ChangePassword", payload);
}

/** GET /api/Token/{sessionId} */
export function GetToken(sessionId) {
  return getAuthClient().get(`Token/${encodeURIComponent(sessionId)}`);
}

export function UserBranch(payload) {
  return getAuthClient().post("User/UserBranch", payload);
}

export function UserByRole(payload) {
  return getAuthClient().post("User/UserByRole", payload);
}

export function GetProducts(payload, options) {
  return getAuthClient().post("Product/GetProducts", payload, {
    silent: options?.silent === true,
  });
}

/** POST /api/Product/AgentRequestInsert — catalog insured person request. */
export function AgentRequestInsert(payload) {
  console.log("[AgentRequestInsert] request", payload);
  return getAuthClient().post("Product/AgentRequestInsert", payload);
}

/** POST /api/Product/GetAgentRequestsByAgent — policies list for current agent. */
export function GetAgentRequestsByAgent(payload) {
  return getAuthClient().post("Product/GetAgentRequestsByAgent", payload);
}

/** POST /api/Product/GetAgentRequestCards — insured cards for agent request (reqId). */
export function GetAgentRequestCards(payload) {
  return getAuthClient().post("Product/GetAgentRequestCards", payload);
}

/** POST /api/Product/GetAgentRequestCardsAgent — insured list for PROD policy detail (reqId). */
export function GetAgentRequestCardsAgent(payload, options) {
  return getAuthClient().post("Product/GetAgentRequestCardsAgent", payload, {
    silent: options?.silent === true,
  });
}

/** POST /api/Product/GetPolicyByIdAgent — contract block for PROD policy detail (reqId). */
export function GetPolicyByIdAgent(payload, options) {
  return getAuthClient().post("Product/GetPolicyByIdAgent", payload, {
    silent: options?.silent === true,
  });
}

/** POST /api/Product/AgentRequestDelete — delete agent request by reqId. */
export function AgentRequestDelete(payload) {
  return getAuthClient().post("Product/AgentRequestDelete", payload);
}

/** POST /api/Product/RetryPolicyIssue — retry when invoice not issued (Invoice buraxılmayıb). */
export function RetryPolicyIssue(payload) {
  return getAuthClient().post("Product/RetryPolicyIssue", payload);
}

/** POST /api/Product/CheckNotificationStatus — was invoice notification sent? */
export function CheckNotificationStatus(payload) {
  return getAuthClient().post("Product/CheckNotificationStatus", payload, { silent: true });
}

/** POST /api/Product/ResendNotification — resend invoice notification. */
export function ResendNotification(payload) {
  return getAuthClient().post("Product/ResendNotification", payload);
}

/** POST /api/Document/DownloadPdf — generate PDF into pdffiles/{type}_{id}.pdf */
export function DownloadPdf(payload) {
  return getAuthClient().post("Document/DownloadPdf", payload);
}

/** POST /api/Statistics/GetAgentStatistics — report totals and by product. */
export function GetAgentStatistics(payload) {
  return getAuthClient().post("Statistics/GetAgentStatistics", payload);
}

/** POST /api/Statistics/GetAgentStatisticsStatus — report by status. */
export function GetAgentStatisticsStatus(payload) {
  return getAuthClient().post("Statistics/GetAgentStatisticsStatus", payload);
}

export function GetPersonInfo(payload) {
  console.log("[GetPersonInfo] request", payload);
  return getAuthClient().post("UniPolicy/GetPersonInfo", payload);
}

/** Policy detail (readonly) — IMS Medical Agent API */
export function PolicyById(payload) {
  return getAuthClient().post("Policy/PolicyById", payload);
}

export function PolicyProgram(payload) {
  return getAuthClient().post("Policy/Program", payload);
}

export function PolicyGroup(payload) {
  return getAuthClient().post("Policy/Group", payload);
}

export function PolicyException(payload) {
  return getAuthClient().post("Policy/PolicyException", payload);
}

export function PolicyCard(payload) {
  return getAuthClient().post("Policy/PolicyCard", payload);
}

/** Product detail (readonly) — IMS Medical Agent API */
export function ProductById(payload, options) {
  return getAuthClient().post("Product/ProductById", payload, {
    silent: options?.silent === true,
  });
}

export function GetProductPrograms(payload, options) {
  return getAuthClient().post("Product/GetProductPrograms", payload, {
    silent: options?.silent === true,
  });
}

export function GetProductGroups(payload, options) {
  return getAuthClient().post("Product/GetProductGroups", payload, {
    silent: options?.silent === true,
  });
}

export function GetProductExcepts(payload, options) {
  return getAuthClient().post("Product/GetProductExcepts", payload, {
    silent: options?.silent === true,
  });
}

function encryptStore(key, value) {
  window.localStorage.setItem(
    key,
    CryptoJS.AES.encrypt(JSON.stringify(value), CRYPT_KEY).toString()
  );
}

function decryptRead(key) {
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    const txt = CryptoJS.AES.decrypt(raw, CRYPT_KEY).toString(CryptoJS.enc.Utf8);
    return JSON.parse(txt);
  } catch {
    return null;
  }
}

/**
 * После успешного Login: подтягивает филиалы и пользователей по роли и шифрует в localStorage.
 * @param {object} loginRow — элемент из response.data.data[0] (roles, user).
 */
export async function persistBranchAndRoleAfterLogin(loginRow) {
  const roles = loginRow && loginRow.roles;
  const roleId =
    Array.isArray(roles) && roles.length > 0 ? roles[0].roleId : 55;

  const branchPayload = { requestCorrelationId: crypto.randomUUID() };
  const rolePayload = {
    requestCorrelationId: crypto.randomUUID(),
    isPublic: false,
    roleId,
  };

  try {
    const [branchRes, byRoleRes] = await Promise.all([
      UserBranch(branchPayload),
      UserByRole(rolePayload),
    ]);
    if (branchRes?.data?.data != null) {
      encryptStore(LS_USER_BRANCH, branchRes.data.data);
    }
    if (byRoleRes?.data?.data != null) {
      encryptStore(LS_USER_BY_ROLE, byRoleRes.data.data);
    }
    window.dispatchEvent(new CustomEvent("medical-agent-session-updated"));
  } catch (e) {
    console.warn("[Medical Agent] UserBranch / UserByRole:", e);
  }
}

export function getStoredUserBranch() {
  return decryptRead(LS_USER_BRANCH);
}

export function getStoredUserByRole() {
  return decryptRead(LS_USER_BY_ROLE);
}

export function clearMedicalAgentSessionExtras() {
  window.localStorage.removeItem(LS_USER_BRANCH);
  window.localStorage.removeItem(LS_USER_BY_ROLE);
}

export default {
  medicalAgentApiBase,
  Login,
  CheckJWT,
  ChangePassword,
  GetToken,
  UserBranch,
  UserByRole,
  GetProducts,
  AgentRequestInsert,
  GetAgentRequestsByAgent,
  GetAgentRequestCards,
  GetAgentRequestCardsAgent,
  GetPolicyByIdAgent,
  AgentRequestDelete,
  RetryPolicyIssue,
  CheckNotificationStatus,
  ResendNotification,
  DownloadPdf,
  GetAgentStatistics,
  GetAgentStatisticsStatus,
  GetPersonInfo,
  PolicyById,
  PolicyProgram,
  PolicyGroup,
  PolicyException,
  PolicyCard,
  ProductById,
  GetProductPrograms,
  GetProductGroups,
  GetProductExcepts,
  persistBranchAndRoleAfterLogin,
  getStoredUserBranch,
  getStoredUserByRole,
  clearMedicalAgentSessionExtras,
};
